import { Box, Typography } from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import Sparkline from "./Sparkline";

/**
 * KpiCard — Phase 6 (Design Gap Audit item F.1) reusable component.
 *
 * Matches the prototype's headline KPI card: a big number, a label, an
 * optional up/down delta, and an optional sparkline in the corner.
 *
 * Data honesty: `delta` and `trend` are both optional and intentionally
 * omitted by the Dashboard for metrics where no real historical data
 * exists (see Sparkline.jsx's comment) — this component never fabricates
 * either, it simply doesn't render them when absent.
 *
 * Props:
 *   value       - the headline number/string (required)
 *   label       - metric name (required)
 *   delta       - optional { value: number, direction: "up"|"down" } — only
 *                 pass this when you have a real prior-period comparison
 *   trend       - optional array of numbers for the sparkline (see Sparkline.jsx)
 *   color       - accent color for the delta/sparkline (default success green)
 *   onClick     - optional, makes the whole card clickable (e.g. navigate to filtered list)
 *   alert       - optional bool, renders the prototype's red "alert" KPI
 *                 style (red-tinted background/border/text) instead of the
 *                 normal white card. Defaults to false — every existing
 *                 usage of this component is unaffected unless explicitly
 *                 opted in.
 *   actionLabel - optional string, renders a small button inside the card
 *                 (matches the prototype's "Work the queue →" button)
 *   onAction    - () => void, called when actionLabel's button is clicked
 *                 (required if actionLabel is passed; must be a real
 *                 navigation/action, never a no-op)
 */
export default function KpiCard({
  value,
  label,
  delta,
  trend,
  color = "#15803d",
  onClick,
  alert = false,
  actionLabel,
  onAction,
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        border: "1px solid",
        borderColor: alert ? "#fecaca" : "divider",
        borderRadius: "12px",
        p: 2,
        pb: actionLabel ? 4.5 : 2,
        bgcolor: alert ? "#fef2f2" : "background.paper",
        position: "relative",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 0.15s ease",
        "&:hover": onClick
          ? { boxShadow: "0 4px 16px rgba(15,27,51,0.10)" }
          : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 800,
            lineHeight: 1,
            color: alert ? "#b91c1c" : "inherit",
          }}
        >
          {value}
        </Typography>
        {trend && <Sparkline data={trend} color={color} />}
      </Box>
      <Typography
        sx={{
          fontSize: 13,
          color: alert ? "#b91c1c" : "text.secondary",
          fontWeight: 600,
          mt: 0.75,
        }}
      >
        {label}
      </Typography>
      {delta && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.75 }}>
          {delta.direction === "up" ? (
            <ArrowUpwardRoundedIcon
              sx={{ fontSize: 14, color: "success.main" }}
            />
          ) : (
            <ArrowDownwardRoundedIcon
              sx={{ fontSize: 14, color: "error.main" }}
            />
          )}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: delta.direction === "up" ? "success.main" : "error.main",
            }}
          >
            {delta.value}
          </Typography>
        </Box>
      )}
      {actionLabel && (
        <Box
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            onAction?.();
          }}
          sx={{
            position: "absolute",
            right: 12,
            bottom: 11,
            border: "none",
            bgcolor: "#dc2626",
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            borderRadius: "6px",
            px: 1.25,
            py: 0.6,
            cursor: "pointer",
            "&:hover": { bgcolor: "#b91c1c" },
          }}
        >
          {actionLabel}
        </Box>
      )}
    </Box>
  );
}
